import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { STRINGS, isLanguage, type Language } from './i18n';
import { getSupportEmail, getSecurityEmail, getLegalEntityName, getFullAddress } from './legalInfoUtils';

// 2026-10-17 (аудит сторов Б10, Р17): страница поддержки (Support URL сторов) — письмо через mailto вместо формы, которая только
// изображала отправку; сроков ответа не обещаем. Пререндерится в dist/contact/{ru,en,es}/ (scripts/prerender.mjs).
// Прежняя страница с формой — backlog/store-audit-wrapper-site-2026-10-17/landing/src/Contact.tsx.
// Тексты — src/locales/<язык>.ts → contact (2026-10-24).

export const Contact: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState<Language>(isLanguage(lang) ? lang : 'en');

  useEffect(() => {
    if (isLanguage(lang)) {
      setCurrentLang(lang);
    }
  }, [lang]);

  const t = STRINGS[currentLang].contact;
  const supportEmail = getSupportEmail();
  const securityEmail = getSecurityEmail();
  const address = getFullAddress();
  const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(t.mailSubject)}`;

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '1.2rem',
    padding: '1.5rem',
    display: 'grid',
    gap: '1rem',
  };
  const linkStyle: React.CSSProperties = { color: 'var(--primary)' };

  return (
    <div
      className="landing"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 20%, var(--color-rgba-59-130-246-16), var(--color-transparent) 35%), radial-gradient(circle at 85% 10%, var(--color-rgba-124-58-237-12), var(--color-transparent) 30%), var(--background)',
        color: 'var(--text)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <Header
        language={currentLang}
        onLanguageChange={(newLang) => {
          setCurrentLang(newLang);
          navigate(`/contact/${newLang}`);
        }}
      />

      <div
        style={{
          flex: 1,
          padding: 'clamp(2.5rem, 5vw, 4rem) clamp(1.4rem, 4vw, 3rem) clamp(3rem, 5vw, 5rem)',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: '56rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div>
            <h1
              style={{
                fontSize: 'var(--font-h1)',
                margin: 0,
                lineHeight: 1.1,
                background: 'linear-gradient(120deg, var(--text) 0%, var(--primary) 70%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'var(--color-transparent)',
                backgroundClip: 'text',
              }}
            >
              {t.title}
            </h1>
            <p style={{ margin: '0.8rem 0 1.4rem', fontSize: 'var(--text-large)', color: 'var(--textSecondary)', lineHeight: 1.6 }}>
              {t.subtitle}
            </p>
            <a className="btn btn--primary" href={mailto} style={{ display: 'inline-block' }}>
              {t.write}
            </a>
            <p style={{ margin: '1rem 0 0', fontSize: 'var(--text-small)', color: 'var(--textTertiary)', lineHeight: 1.5 }}>
              {t.tip}
            </p>
          </div>

          <div style={cardStyle}>
            <h2 style={{ margin: 0, fontSize: 'var(--font-h2)', fontWeight: 700, color: 'var(--text)' }}>{t.info.title}</h2>
            <div style={{ display: 'grid', gap: '0.75rem', fontSize: 'var(--text-small)' }}>
              <div>
                <strong style={{ color: 'var(--text)' }}>{t.info.email}:</strong>{' '}
                <a href={mailto} style={linkStyle}>
                  {supportEmail}
                </a>
              </div>
              {securityEmail !== supportEmail && (
                <div>
                  <strong style={{ color: 'var(--text)' }}>{t.info.security}:</strong>{' '}
                  <a href={`mailto:${securityEmail}`} style={linkStyle}>
                    {securityEmail}
                  </a>
                </div>
              )}
              <div>
                <strong style={{ color: 'var(--text)' }}>{t.info.legalName}:</strong>{' '}
                <span style={{ color: 'var(--textSecondary)' }}>{getLegalEntityName()}</span>
              </div>
              {address && (
                <div>
                  <strong style={{ color: 'var(--text)' }}>{t.info.address}:</strong>{' '}
                  <span style={{ color: 'var(--textSecondary)' }}>{address}</span>
                </div>
              )}
            </div>
          </div>

          <div style={cardStyle}>
            <h2 style={{ margin: 0, fontSize: 'var(--font-h2)', fontWeight: 700, color: 'var(--text)' }}>{t.refunds.title}</h2>
            <p style={{ margin: 0, fontSize: 'var(--text-body)', color: 'var(--textSecondary)', lineHeight: 1.6 }}>{t.refunds.description}</p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: 'var(--text-small)' }}>
              <span>
                <strong style={{ color: 'var(--text)' }}>{t.refunds.apple}:</strong>{' '}
                <a href={t.refunds.appleUrl} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, textDecoration: 'underline' }}>
                  {t.refunds.link}
                </a>
              </span>
              <span>
                <strong style={{ color: 'var(--text)' }}>{t.refunds.google}:</strong>{' '}
                <a href={t.refunds.googleUrl} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, textDecoration: 'underline' }}>
                  {t.refunds.link}
                </a>
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 'var(--text-small)', color: 'var(--textTertiary)', lineHeight: 1.5 }}>{t.refunds.note}</p>
          </div>
        </div>
      </div>

      <Footer language={currentLang} />
    </div>
  );
};
